import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import SaveRestaurantModal from '../../restaurant/SaveRestaurantModal';
import DeleteRestaurantModal from '../../restaurant/DeleteRestaurantModal';
import { RestaurantSectionsList } from '../../restaurant/RestaurantSectionsList';
import { RestaurantRevenueSourcesList } from '../../restaurant/RestaurantRevenueSourcesList';
import { RestaurantBorkConnection } from '../../restaurant/RestaurantBorkConnection';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantLogo from '../../ui/RestaurantLogo';

const RestaurantDetailPage: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const restaurants = useRestaurantStore(s => s.restaurants);
  const loadAllData = useRestaurantStore(s => s.loadAllData);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const restaurant = restaurants.find(r => r.id === restaurantId);

  useEffect(() => {
    if (restaurants.length === 0) {
      loadAllData().catch(err => {
        console.error(err);
        setError('Failed to load restaurant data');
      });
    }
  }, [loadAllData, restaurants.length]);

  const handleSaveRestaurant = async () => {
    await loadAllData(true);
    setIsEditModalOpen(false);
  };

  const handleDeleteRestaurant = () => {
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    navigate('/profile');
  };

  if (!restaurant && restaurants.length > 0) {
    return (
      <main className="min-h-[calc(100vh-5rem)] text-text dark:text-text-dark p-4">
        <div className="max-w-6xl mx-auto">
          <p className="text-center">{t('restaurantDetail.notFound')}</p>
          <button
            onClick={() => navigate('/profile')}
            className="mt-4 mx-auto block px-4 py-2 rounded-2xl bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition"
          >
            {t('restaurantDetail.backToProfile')}
          </button>
        </div>
      </main>
    );
  }

  if (!restaurant) {
    return (
      <main className="min-h-[calc(100vh-5rem)] text-text dark:text-text-dark p-4">
        <div className="max-w-6xl mx-auto">
          <p className="text-center">{t('restaurantDetail.loading')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-[calc(100vh-5rem)] text-text dark:text-text-dark p-4">
      <MobilePageHeader
        title={restaurant.name}
        titleLeading={<RestaurantLogo logoUrl={restaurant.logoUrl} name={restaurant.name} size="small" />}
      />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <button
            onClick={() => navigate('/profile')}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            aria-label={t('restaurantDetail.goBack')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 hidden lg:flex items-center gap-3 min-w-0">
            <RestaurantLogo logoUrl={restaurant.logoUrl} name={restaurant.name} size="medium" />
            <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50 truncate">
              {restaurant.name}
            </h1>
          </div>

          {/* Mobile-only spacer so the edit/delete actions stay right-aligned once the h1 is hidden */}
          <div className="flex-1 lg:hidden" aria-hidden="true" />

          <div className="flex gap-2">
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label={t('restaurantDetail.editRestaurant')}
            >
              <Pencil className="w-5 h-5" />
            </button>
            {restaurants.length > 1 && (
              <button
                onClick={handleDeleteRestaurant}
                className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition"
                aria-label={t('restaurantDetail.deleteRestaurant')}
              >
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Revenue Config Section */}
        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-50">
              {t('restaurantDetail.revenueConfig')}
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              {t('restaurantDetail.revenueConfigDescription')}
            </p>
          </div>
          <div className="bg-card dark:bg-card-dark rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6">
            <RestaurantRevenueSourcesList restaurantId={restaurant.id} />
            {/* Renders itself only for restaurants with a trivecgroup revenue source. */}
            <RestaurantBorkConnection restaurantId={restaurant.id} />
          </div>
        </div>

        {/* Supplier Config Section */}
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-50">
              {t('restaurantDetail.supplierConfig')}
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              {t('restaurantDetail.supplierConfigDescription')}
            </p>
          </div>
          <RestaurantSectionsList restaurantId={restaurant.id} />
        </div>
      </div>

      {/* Edit Restaurant Modal */}
      <SaveRestaurantModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mode="edit"
        initialId={restaurant.id}
        initialName={restaurant.name}
        initialLogoUrl={restaurant.logoUrl}
        onLogoChanged={() => loadAllData(true)}
        onSave={handleSaveRestaurant}
      />

      {/* Delete Confirmation Modal */}
      <DeleteRestaurantModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        restaurantId={restaurant.id}
        restaurantName={restaurant.name}
        onDelete={handleDelete}
      />
    </main>
  );
};

export default RestaurantDetailPage;
